import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'
import { Component, Prop, Watch } from 'vue-property-decorator'
import * as types from '@board/store/types'
import * as accountTypes from '@account/store/types'
import { getProperty, getLangProperty } from '@appbase/util'
import { createEventBus, removeEventBus } from '@appbase/util/eventBus'
import { loadScript } from '@appbase/util/script-loader'
import boardApi from '@board/api'
import { open, OpenType } from '@board/open-handler'
import api from '@board/api'

import SearchBox from '@board/components/common/SearchBox.vue'
import Paginate from '@board/components/common/Paginate.vue'
import BoardWritePage from '@board/components/board/BoardWritePage.vue'
import { LoadingBar } from '@appbase/components/loading'
import ReservedListModal from '@board/views/HK_ReservedArticles/ReservedArticlesModal.vue'

@Component({
    components: {
        SearchBox,
        Paginate,
        BoardWritePage,
        LoadingBar,
        ReservedListModal
    },
    computed: mapGetters({        
        popupBaseUrl: types.POPUP_BASEURL,
        boardConfig: types.BOARD_CONFIG,
        contentFields: types.FETCH_CONTENTFIELD,
        pageSet: types.FETCH_PAGESET,
        user: accountTypes.USER,
        current: accountTypes.CURRENT
    }),
    methods: {
        ...mapActions({
            fetchBoard: types.FETCH_BOARD,
            fetchArticle: types.FETCH_ARTICLES,
            fetchBlogArticle: types.FETCH_BLOGARTICLES,
            fetchPageSet: types.FETCH_PAGESET,
        }),
    },
})
export default class BoardMain extends Vue {
    @Prop({ type: Boolean }) adminMode!: boolean

    fetchBoard!: (boardId: number) => Promise<board.BoardItem>
    fetchArticle!: (searchset: article.SearchSet) => Promise<article.PagedData<article.SearchSet>>
    fetchBlogArticle!: (searchset: article.SearchSet) => Promise<article.PagedData<article.SearchSet>>
    fetchPageSet!: (pageSet: boardModule.state.PageSet) => void
    
    user!: account.User
    popupBaseUrl!: string
    pageSet!: boardModule.state.PageSet
    boardConfig!: board.WriteConfig
    contentFields!: board.ContentField[]
    current!: account.TCurrent

    writable: boolean = true;
    listable: boolean = true;
    permissionMounted: boolean = false;
    totalPage: number = 0
    totalCount: number = 0
    itemsPerPage: number = 15
    currentPath: string = ''
    currentBoard: number = 0
    currentArticle: number = 0
    ofString: string = ''
    selectedArticles: article.Article[] = []

    get boardName() {
        if (this.boardConfig == null) return ''
        else return getLangProperty(this.boardConfig, 'boardName')
    }

    get searchBoxCriterias() {
        if (this.boardConfig == null) {
            return []
        } else {
            var searchFields = this.contentFields.filter((f) => f.isSearchField)
            
            return searchFields.map(
                (m) =>
                    <common.SearchBoxCriteria>{
                        value: m.fieldId.toString(),
                        displayName: getLangProperty(m, 'displayName'),
                    }
            )            
        }
    }  

    get showNavigatePane() {
        if (this.boardConfig == null || this.adminMode) {
            return false
        } else {
            return !this.boardConfig.useFixedView
        }
    }

    get propWritable() {
        return this.writable;
    }

    @Watch('boardConfig')
    onBoardConfigChanged(newVal: board.BoardItem) {
        const { language } = this.current.userConfig
        const title = language === 'ko-KR' ? newVal.boardName : newVal.boardName_En
        this.setPage({ title })
    }

    @Watch('$route.fullPath')
    onRouteChanged(newVal: string) {
        var { SYSID } = this.$route.query        
        
        if (
            this.boardConfig == null || 
            (SYSID && this.boardConfig.boardId != Number(SYSID)) || 
            this.$route.name === 'boardmodule-main') {                                
            this.mountAction()    
        }

        this.currentPath = this.$route.name || "";
    }

    created() {
        var _this = this
        createEventBus(window, 'boardlist-reload', this.handleReload)

        if ((window as any).eumBoardIds) {
            this.mountAction()
        } else {
            loadScript('/cm/gres/js/boardFieldSetting.js', 'eum-board-setting', function () {
                _this.mountAction()
            })
        }
    }

    beforeDestroy() {
        removeEventBus(window, 'boardlist-reload', this.handleReload)
    }

    handleReload() {
        this.fetchPageSet(<boardModule.state.PageSet>{
            boardId: this.currentBoard,
            currentPage: this.pageSet.currentPage,
            searchField: this.pageSet.searchField,
            searchText: this.pageSet.searchText,
            preface1: this.pageSet.preface1,
            preface2: this.pageSet.preface2,
            useNotice: this.pageSet.useNotice,
        })
    }

    mountAction() {
        if (this.$route.query) {
            var query = this.$route.query

            if (!query.SYSID) {
                return
            }

            this.currentBoard = Number(query.SYSID)                                  
            this.permissionMounted = false;

            if (this.boardConfig == null || !(this.boardConfig.boardId == this.currentBoard)) {
                Promise.all([
                    this.fetchBoard(this.currentBoard),
                    api.article.getSystemAuth(this.currentBoard)
                ]).then((res: any) => {
                    this.mountPermission(res[0], res[1]);
                    this.determineViewRoute(res[0])
                }).finally(() => { this.permissionMounted = true })                
            } else {       
                api.article.getSystemAuth(this.currentBoard).then(res => {
                    this.mountPermission(this.boardConfig, res);
                    this.determineViewRoute(this.boardConfig)
                }).finally(() => { this.permissionMounted = true })
            }          
        }

        this.currentPath = this.$route.name || "";
    }

    mountPermission(writeConfig: board.WriteConfig, auth: article.SystemAuth[]) {
        var superAdmin: boolean = this.current.permissions.isSuperAdmin;
        var companyAdmin: boolean = this.current.permissions.isCompanyManager && writeConfig.comCode === this.user.comCode;
        var boardAdmin: boolean = writeConfig.boardManagers.findIndex(m => m.manager === this.user.personCode) >= 0;
        var menuAdmin: boolean = auth.findIndex(a => a.systemId === this.currentBoard && a.isMenuAdmin) >= 0

        if(superAdmin || companyAdmin || boardAdmin || menuAdmin) {
            this.writable = true;
            this.listable = true;
        } else {
            if(writeConfig.permission.roleName !== 'Default') {
                console.log('permission', writeConfig.permission);
                this.writable = writeConfig.permission.write;
                this.listable = writeConfig.permission.read;                
            } else {
                var currentBoardAuth = auth.find(a => a.systemId === this.currentBoard);
                console.log('currentAuth', currentBoardAuth);
     
                this.writable = currentBoardAuth?.write || false;
                this.listable = currentBoardAuth?.read || false;               
            }    
        }    
        
        this.permissionMounted = true;
    }

    determineViewRoute(config: board.Board) {
        if(!this.listable)
            return;

        if (this.$route.path.endsWith('/read') || this.$route.path.endsWith('/write')) 
            return;

        var routeName = 'boardmodule-listview'

        if (this.adminMode) {
            routeName = 'boardmodule-adminlistview'
        } else {
            switch (config.defaultView) {
                case 'AL':
                    routeName = 'boardmodule-albumview'
                    break
                case 'BL':
                    routeName = 'boardmodule-summaryview'
                    break
                default:
                    routeName = 'boardmodule-listview'
                    break
            }
        }
       
        this.$router.push({
            name: routeName,
            query: this.$route.query,
        })            
        .catch((err) => { });           
    }

    getViewBtnClass(name: string) {
        var btnClass: any = {};
        if(this.currentPath === name) {
            btnClass["btn-basic"] = true;
        } else {
            btnClass["btn-basic-outline"] = true;
        }        
        return btnClass;
    }

    getArticleFieldValue(article: article.Article, field: board.BoardField) {
        if (field.fieldValueName != null) {
            return getProperty(article, field.fieldValueName)
        } else {
            var FieldValue = article.fieldValues.find((f) => f.fieldId == field.fieldId)
            return FieldValue?.value
        }
    }

    pageClickCallback(page: number) {
        this.fetchPageSet(<boardModule.state.PageSet>{
            boardId: this.currentBoard,
            currentPage: page,
            searchField: this.pageSet.searchField,
            searchText: this.pageSet.searchText,
            preface1: this.pageSet.preface1,
            preface2: this.pageSet.preface2,
            useNotice: this.pageSet.useNotice,
        })
    }

    searchClick(
        selected: string,
        searchText: string,
        preface1: string | null,
        preface2: string | null
    ) {
        this.fetchPageSet(<boardModule.state.PageSet>{
            boardId: this.currentBoard,
            currentPage: 1,
            searchField: Number(selected),
            searchText: searchText,
            preface1: preface1,
            preface2: preface2,
            useNotice: this.boardConfig.usePrimaryNotice,
        })
    }

    prefaceChanged(preface1: string | null, preface2: string | null) {
        this.fetchPageSet(<boardModule.state.PageSet>{
            boardId: this.currentBoard,
            currentPage: 1,
            searchField: 0,
            searchText: null,
            preface1: preface1,
            preface2: preface2,
            useNotice: this.boardConfig.usePrimaryNotice,
        })
    }

    onArticlesChanged(itemsPerPage: number, totalCount: number, page: number, itemCount: number) {
        this.totalCount = totalCount
        this.totalPage = Math.ceil(totalCount / itemsPerPage)

        var startIdx = (page - 1) * itemsPerPage + 1
        this.ofString = `${startIdx} - ${startIdx + itemCount - 1} of ${totalCount}`
    }

    goWritePage() {      
        open(!this.isTeams && (this.boardConfig.usePopup || this.adminMode) ?
            OpenType.PopupWrite : OpenType.ModalWrite, {
            boardId: this.boardConfig.boardId,
            baseUrl: this.popupBaseUrl,
            query:{"MENUID":this.$route.query.MENUID as string??""}
        })
    }

    async onRestoreClick() {
        if (await (window as any).confirmAsync(this.$t('선택_게시글_복원').toString())) {
            boardApi.article
                .restoreArticles(this.selectedArticles.map((a) => a.articleId))
                .then(async (res) => {
                    await (window as any).alertAsync(this.$t('복원_완료').toString());
                })
                .catch(async (err) => {
                    await (window as any).alertAsync(this.$t('복원_실패').toString())
                })
                .finally(async () => {
                    this.handleReload()
                })
        }
    }

    async onDeleteClick(hardDelete: boolean) {
        var confirmMessage = hardDelete
            ? this.$t('선택_게시글_완전삭제').toString()
            : this.$t('선택_게시글_삭제').toString();

        if (await (window as any).confirmAsync(confirmMessage)) {
            boardApi.article
                .deleteArticles({
                    ids: this.selectedArticles.map((a) => a.articleId),
                    pernamentDelete: hardDelete,
                })
                .then(async (res) => {
                    await (window as any).alertAsync(this.$t('M_Common_Deleted').toString());
                })
                .catch(async (err) => {
                    await (window as any).alertAsync(this.$t('삭제실패').toString());
                })
                .finally(async () => {
                    this.handleReload()
                })
        }
    }

    async onToggleSecretClick() {
        if (await (window as any).confirmAsync(this.$t('공개여부_전환_확인').toString())) {
            boardApi.article
                .toggleArticlesSecret(this.selectedArticles.map((a) => a.articleId))
                .then(async (res) => {
                    await (window as any).alertAsync(this.$t('전환_완료').toString());
                })
                .catch(async (err) => {
                    await (window as any).alertAsync(this.$t('전환_실패').toString())
                })
                .finally(async () => {
                    this.handleReload()
                })
        }
    }

    onLayoutChangeClick(type: string) {
        this.$router.push({
            name: type,
            query: this.$route.query,
        })
    }

    showReservedList(){
        const modal: appbase.IModalOpenable = this.$refs['modal'] as any
        const options: appbase.TModalInfo<any> = {
          component: ReservedListModal,
          props: {boardId: Number(this.$route.query.SYSID)},
          events: {
            cancel: () => {
              modal.closeModal()
            },
          },
        }
        modal.openModal(options)
    }
}
